import * as authHooks from "@rentiohq/shared-frontend/dist/redux/auth/auth.hooks";
import * as applicationHooks from "@rentiohq/shared-frontend/dist/reduxV2/applications/application.hooks";
import * as bankAccountHooks from "@rentiohq/shared-frontend/dist/reduxV2/bankAccount/bankAccount.hooks";
import * as contractHooks from "@rentiohq/shared-frontend/dist/reduxV2/contract/contract.hooks";
import * as propertyHooks from "@rentiohq/shared-frontend/dist/reduxV2/property/property.hooks";
import * as templateSelectors from "@rentiohq/shared-frontend/dist/reduxV2/template/template.selectors";
import * as templateUtils from "@rentiohq/shared-frontend/dist/reduxV2/template/template.utils";
import { formatAddress } from "@rentiohq/shared-frontend/dist/utils/address.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { Error, Loading, Page } from "@rentiohq/web-shared/dist/components";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { IRootStore } from "redux/reducers";
import * as t from "../../../../../../services/translationService";
import { getAvailableTemplatesForType } from "../../components/ContractCard/ContractCard.utils";
import { getTenantsFromApplications } from "../../Contracts.utils";
import { BasicContractForm } from "./BasicContractForm";

interface IProps {
  deleteCancellation?: boolean;
}

export const ContractInfo = ({ deleteCancellation = false }: IProps) => {
  const params = useParams<{ contractId: string; propertyId: string }>();
  const { contractId } = params;

  const { isBroker } = authHooks.useSelf();

  const {
    detail: contract,
    isFetching: isFetchingContract,
    fetchError: contractFetchError,
  } = contractHooks.useDetail({
    shouldRefetch: false,
    id: contractId,
  });

  const propertyId = params.propertyId ? +params.propertyId : undefined;
  const {
    detail: property,
    isFetching: isFetchingProperty,
    fetchError: propertyFetchError,
  } = propertyHooks.useDetail({
    shouldRefetch: false,
    id: propertyId,
  });

  const {
    detail: ibanisationBankAccount,
    isFetching: isFetchingIbanisationBankAccount,
  } = bankAccountHooks.useDetail({
    shouldRefetch: false,
    customPath: contract?.id
      ? `/contracts/${contract?.id}/ibanisation-bank-account`
      : undefined,
  });

  const {
    items: applications,
    isFetching: isFetchingApplications,
    fetchError: fetchApplicationsError,
  } = applicationHooks.useGetAll({
    query: {
      filter: { propertyId: { eq: propertyId }, archivedAt: { is: null } },
      sort: [{ field: "providedAt", method: "DESC" }],
    },
    shouldRefetch: true,
  });

  const tenants = getTenantsFromApplications(applications);

  const templatesQuery = templateUtils.getTemplatesQuery();

  const isFetching = useSelector((state: IRootStore) => {
    return (
      isFetchingContract ||
      isFetchingProperty ||
      isFetchingIbanisationBankAccount ||
      isFetchingApplications ||
      templateSelectors.getPaged(state, templatesQuery)?.isFetching
    );
  });

  const fetchError =
    contractFetchError || propertyFetchError || fetchApplicationsError;

  const templates = useSelector(
    (state: IRootStore) =>
      templateSelectors.getPaged(state, templatesQuery)?.items,
  );

  const availableTemplatesForContract = useMemo(() => {
    return property
      ? getAvailableTemplatesForType({
          type: "contract",
          isBroker,
          templates,
          contract,
          property,
        })
      : undefined;
  }, [property]);

  if (isFetching) {
    return <Loading />;
  }

  if (!!fetchError) {
    return <Error errors={[fetchError]} />;
  }

  if (!property) {
    return null;
  }

  return (
    <Page
      title={
        contract
          ? getLocalizedText("contract_info.update.heading")
          : getLocalizedText("contract_info.create.heading")
      }
      subtitle={formatAddress(property, false)}
      breadcrumbs={{
        to: `/properties/${property.id}/contracts`,
        content: t.contractDetailBreadcrumb(),
      }}
    >
      <BasicContractForm
        property={property}
        contract={contract}
        tenants={tenants}
        ibanisationBankAccount={ibanisationBankAccount}
        availableTemplatesForContract={availableTemplatesForContract}
        deleteCancellation={deleteCancellation}
      />
    </Page>
  );
};
